<template>
  <div
    class="search-select"
    :class="{
      'search-select--disabled': disabled,
      'search-select--error': !!error,
      'search-select--open': isOpen,
    }"
  >
    <label v-if="label" :for="inputId" class="search-select__label">{{ label }}</label>

    <div class="search-select__control">
      <input
        :id="inputId"
        ref="input"
        class="search-select__input terrain-focusable"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOptionId"
        :aria-invalid="!!error"
        :disabled="disabled"
        :placeholder="placeholder"
        :value="query"
        @focus="handleFocus"
        @input="handleInput"
        @keydown="handleKeydown"
        @blur="handleBlur"
      >
      <span v-if="loading" class="search-select__spinner" aria-hidden="true"></span>
      <button
        v-else-if="clearable && value !== null && value !== undefined && !disabled"
        type="button"
        class="search-select__clear"
        aria-label="Limpiar selección"
        @mousedown.prevent="clear"
      >×</button>
      <span class="search-select__chevron" aria-hidden="true">▾</span>
    </div>

    <ul
      v-if="isOpen"
      :id="listboxId"
      class="search-select__listbox terrain-panel"
      role="listbox"
      aria-live="polite"
    >
      <li
        v-if="showCreateOption"
        class="search-select__option search-select__option--create"
        role="option"
        :aria-selected="false"
        @mousedown.prevent="createOption"
      >
        Crear "{{ trimmedQuery }}"
      </li>
      <li v-if="!filteredOptions.length && !showCreateOption" class="search-select__empty">
        <slot name="empty">{{ query ? 'Sin resultados' : emptyMessage }}</slot>
      </li>
      <li
        v-for="(option, index) in filteredOptions"
        :id="optionId(option)"
        :key="option[idKey]"
        class="search-select__option"
        :class="{
          'search-select__option--active': index === activeIndex,
          'search-select__option--selected': option[idKey] === value,
        }"
        role="option"
        :aria-selected="option[idKey] === value"
        @mousedown.prevent="selectOption(option)"
        @mouseenter="activeIndex = index"
      >
        <slot name="option" :option="option">
          <span class="search-select__option-label">{{ option[labelKey] }}</span>
          <span v-if="option.sublabel" class="search-select__option-sublabel">{{ option.sublabel }}</span>
        </slot>
      </li>
    </ul>

    <p v-if="error" class="terrain-error-text" role="alert">{{ error }}</p>
  </div>
</template>

<script>
// SearchSelect.vue — combobox de selección única con búsqueda.
//
// Diseño visual nuevo (no reutiliza AutoCompleteInput.vue): controlado por
// v-model (value/input) sobre un id, con navegación por teclado, estados de
// carga/error/deshabilitado y filtrado dual — filtra `options` en cliente Y
// emite `search` (debounced) para que el padre pueda resolver server-side,
// según lo que necesite cada consumidor (plantillas, versiones, etc.).
let uidCounter = 0;

export default {
  name: 'SearchSelect',
  props: {
    value: { type: [String, Number], default: null },
    options: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    error: { type: String, default: '' },
    placeholder: { type: String, default: 'Buscar…' },
    label: { type: String, default: '' },
    labelKey: { type: String, default: 'label' },
    idKey: { type: String, default: 'id' },
    clearable: { type: Boolean, default: true },
    emptyMessage: { type: String, default: 'Sin opciones disponibles' },
    // allowCreate: habilita el ítem "Crear "{texto}"" en el listbox cuando
    // lo tecleado no matchea ningún option[labelKey] existente — opt-in
    // (default false) para no afectar los consumidores actuales
    // (Plantilla/Versión en TerrainCampaignEdit.vue, TerrainCrewForm.vue),
    // que son selección cerrada sobre un catálogo, no crean nada al vuelo.
    allowCreate: { type: Boolean, default: false },
  },
  data() {
    return {
      uid: `search-select-${++uidCounter}`,
      query: '',
      isOpen: false,
      isEditing: false,
      activeIndex: 0,
      searchTimer: null,
    };
  },
  computed: {
    inputId() { return `${this.uid}-input`; },
    listboxId() { return `${this.uid}-listbox`; },
    filteredOptions() {
      if (!this.isEditing) return this.options || [];
      const query = (this.query || '').trim().toLowerCase();
      if (!query) return this.options || [];
      return (this.options || []).filter((option) => {
        const label = String(option[this.labelKey] || '').toLowerCase();
        const sublabel = String(option.sublabel || '').toLowerCase();
        return label.includes(query) || sublabel.includes(query);
      });
    },
    activeOptionId() {
      const option = this.filteredOptions[this.activeIndex];
      return option ? this.optionId(option) : null;
    },
    trimmedQuery() { return (this.query || '').trim(); },
    hasExactMatch() {
      if (!this.trimmedQuery) return false;
      const target = this.trimmedQuery.toLowerCase();
      return (this.options || []).some((option) => String(option[this.labelKey] || '').toLowerCase() === target);
    },
    // showCreateOption: no se limita a "sin resultados" — escribir "Salud"
    // con "Salud Pública" ya existente igual debe poder crear "Salud" si es
    // lo que se quiere, así que solo exige que no haya un match EXACTO.
    showCreateOption() {
      return this.allowCreate && this.isEditing && Boolean(this.trimmedQuery) && !this.hasExactMatch;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(id) {
        const found = (this.options || []).find((option) => option[this.idKey] === id);
        this.query = found ? found[this.labelKey] : '';
      },
    },
    options() {
      const found = (this.options || []).find((option) => option[this.idKey] === this.value);
      if (found) this.query = found[this.labelKey];
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    optionId(option) { return `${this.uid}-option-${option[this.idKey]}`; },
    handleFocus() {
      this.isOpen = true;
      this.isEditing = false;
      this.activeIndex = 0;
      this.$emit('open');
    },
    handleInput(event) {
      this.query = event.target.value;
      this.isEditing = true;
      this.isOpen = true;
      this.activeIndex = 0;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.$emit('search', this.query), 250);
    },
    handleBlur() {
      this.isOpen = false;
      this.isEditing = false;
      const found = (this.options || []).find((option) => option[this.idKey] === this.value);
      this.query = found ? found[this.labelKey] : '';
      this.$emit('close');
    },
    handleKeydown(event) {
      if (this.disabled) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        if (!this.isOpen) { this.isOpen = true; return; }
        this.activeIndex = Math.min(this.activeIndex + 1, this.filteredOptions.length - 1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const option = this.filteredOptions[this.activeIndex];
        if (option) this.selectOption(option);
      } else if (event.key === 'Escape') {
        this.isOpen = false;
        this.isEditing = false;
        if (this.$refs.input) this.$refs.input.blur();
      }
    },
    selectOption(option) {
      this.$emit('input', option[this.idKey]);
      this.query = option[this.labelKey];
      this.isOpen = false;
      this.isEditing = false;
      this.$emit('close');
      if (this.$refs.input) this.$refs.input.blur();
    },
    clear() {
      this.$emit('input', null);
      this.query = '';
      this.isEditing = false;
      if (this.$refs.input) this.$refs.input.focus();
    },
    // createOption: el padre decide cómo materializar la opción nueva
    // (agregarla a `options` + setear `value`) — este componente se queda
    // genérico y no asume la forma del id.
    createOption() {
      if (!this.trimmedQuery) return;
      this.$emit('create', this.trimmedQuery);
      this.isOpen = false;
      this.isEditing = false;
      this.$emit('close');
      if (this.$refs.input) this.$refs.input.blur();
    },
  },
};
</script>

<style lang="scss" scoped>
.search-select {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--terrain-space-xs);
  width: 100%;

  &__label {
    font-size: var(--terrain-font-caption);
    font-weight: 600;
    color: var(--color-navy);
  }

  &__control {
    display: flex;
    align-items: center;
    gap: var(--terrain-space-sm);
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    background: var(--color-white);
    border: 1px solid var(--color-line);
    border-radius: var(--terrain-radius-md);
    transition: border-color var(--terrain-transition), box-shadow var(--terrain-transition);

    &:hover { border-color: var(--color-steel); }
    &:focus-within { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
  }

  &--open .search-select__control { border-color: var(--color-steel); box-shadow: var(--terrain-shadow-sm); }
  &--error .search-select__control { border-color: var(--color-danger); }
  &--disabled .search-select__control { background: var(--color-mist); cursor: not-allowed; }
  &--open .search-select__chevron { transform: rotate(180deg); }

  &__input {
    flex: 1;
    min-width: 0;
    border: none;
    padding: 0;
    background: transparent;
    font-size: var(--terrain-font-body);
    color: var(--color-graphite);

    &:disabled { cursor: not-allowed; color: var(--color-ghost); }
    &:focus { outline: none; }
  }

  &__clear {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--color-ghost);
    font-size: 0.95rem;
    line-height: 1;
    cursor: pointer;
    transition: background-color var(--terrain-transition), color var(--terrain-transition);

    &:hover { background: var(--color-mist); color: var(--color-danger); }
  }

  &__chevron {
    color: var(--color-ghost);
    font-size: 0.7rem;
    transition: transform var(--terrain-transition);
  }

  &__spinner {
    width: 14px;
    height: 14px;
    border: 2px solid var(--color-line);
    border-top-color: var(--color-steel);
    border-radius: 50%;
    animation: search-select-spin 0.7s linear infinite;
  }

  &__listbox {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 20;
    max-height: 260px;
    overflow-y: auto;
    padding: var(--terrain-space-xs) 0;
    margin: 0;
    list-style: none;
  }

  &__option {
    display: flex;
    flex-direction: column;
    padding: var(--terrain-space-sm) var(--terrain-space-md);
    cursor: pointer;
    transition: background-color var(--terrain-transition);

    &:hover,
    &--active {
      background: var(--color-mist);
    }

    &--selected &-label {
      color: var(--color-navy);
      font-weight: 600;
    }

    &-label {
      font-size: var(--terrain-font-body);
      color: var(--color-graphite);
    }

    &-sublabel {
      font-size: var(--terrain-font-caption);
      color: var(--color-ghost);
    }

    &--create {
      color: var(--color-navy);
      font-weight: 600;
    }
  }

  &__empty {
    padding: var(--terrain-space-md);
    color: var(--color-ghost);
    font-size: var(--terrain-font-body);
    font-style: italic;
  }
}

@keyframes search-select-spin {
  to { transform: rotate(360deg); }
}
</style>
